import { Bubble } from '../components/Bubble';
import { type LocationStatus, useLocation } from '../state/location';

interface Copy {
  emoji: string;
  title: string;
  body: string;
  action?: 'request' | 'refresh';
  label?: string;
}

const COPY: Record<LocationStatus, Copy> = {
  checking: { emoji: '🛰️', title: 'Finding you…', body: 'Checking location access.' },
  serviceDisabled: {
    emoji: '📡',
    title: 'Turn on location',
    body: "EthrHunt is played in the real world, so it needs your device's location services switched on.",
    action: 'request',
    label: 'Try again',
  },
  denied: {
    emoji: '📍',
    title: 'EthrHunt needs your location',
    body: 'Items hide in real places. Allow location access so we can see what is around you. We never store your exact coordinates.',
    action: 'request',
    label: 'Allow location',
  },
  deniedForever: {
    emoji: '🔒',
    title: 'Location is blocked',
    body: 'Your browser is blocking location for EthrHunt. Open the site settings (the icon next to the address bar, or your phone\'s app settings for EthrHunt), allow Location, then come back.',
    action: 'refresh',
    label: 'I allowed it, check again',
  },
  granted: { emoji: '✅', title: 'All set', body: 'Loading…' },
};

/** Blocking screen. EthrHunt is unplayable without GPS, so nothing else renders until location is allowed. */
export function LocationGateScreen({ status }: { status: LocationStatus }) {
  const { request, refresh } = useLocation();
  const c = COPY[status];
  return (
    <div className="center-fill" data-testid="location-gate">
      <Bubble style={{ padding: 28, maxWidth: 420 }}>
        <div style={{ fontSize: 72 }} aria-hidden="true">{c.emoji}</div>
        <h1 className="t-headline" style={{ margin: '16px 0 12px' }}>{c.title}</h1>
        <p>{c.body}</p>
        <div style={{ marginTop: 24 }}>
          {status === 'checking' ? (
            <span className="spinner dark" role="progressbar" aria-label="Checking" />
          ) : c.action ? (
            <>
              <button type="button" className="btn filled" onClick={() => void (c.action === 'request' ? request() : refresh())}>
                📍 {c.label}
              </button>
              {c.action === 'request' && (
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="btn text" onClick={() => void refresh()}>I turned it on, check again</button>
                </div>
              )}
            </>
          ) : null}
        </div>
      </Bubble>
    </div>
  );
}
